import { getLLMCategoryIconSuggestion } from '@easy-expense/auth-client';
import { CategoryValue } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { Checkbox } from '@mui/material';
import React from 'react';

import { IconPicker } from './IconPicker.component';
import { Button } from '../Button.components';
import { LabelTextField } from '../LabelTextField.component';

const DEFAULT_ICON = '💸';

export const UpsertCategory: React.FC<{
  onDoneUpsertCategory: () => void;
  onDiscard: () => void;
  selectedCategoryKey?: string;
}> = ({ onDoneUpsertCategory, onDiscard, selectedCategoryKey }) => {
  const category = Data.expenseCategories.useByKey(selectedCategoryKey);
  const [categoryValue, setCategoryValue] = React.useState<Omit<CategoryValue, 'color'>>({
    icon: category?.value?.icon ?? DEFAULT_ICON,
    name: category?.value?.name ?? '',
    deductible: category?.value?.deductible ?? false,
    glCode: category?.value?.glCode ?? undefined,
    tipEligible: category?.value?.tipEligible ?? null,
  });
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const cachedCategories = Data.expenseCategories.use();
  const [categoryInputFocused, setCategoryInputFocused] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (category) {
      setCategoryValue({
        icon: category.value.icon ?? DEFAULT_ICON,
        name: category.value.name ?? '',
        deductible: category.value.deductible ?? false,
        glCode: category.value.glCode ?? undefined,
        tipEligible: category.value.tipEligible ?? null,
      });
    } else {
      setCategoryValue({
        icon: DEFAULT_ICON,
        name: '',
        deductible: false,
        glCode: undefined,
        tipEligible: null,
      });
    }
  }, [category?.key]);

  React.useEffect(() => {
    setErrorMessage('');

    if (categoryValue.name === category?.value?.name) {
      return;
    }

    const doesSameNameCategoryExist = cachedCategories.some(
      (category) => category.value.name === categoryValue.name,
    );

    if (doesSameNameCategoryExist) {
      setErrorMessage('A category with the same name already exists');
    }
  }, [categoryValue.name]);

  function onSave() {
    if (errorMessage) {
      return;
    }

    if (category) {
      Data.expenseCategories.update(category.key, { value: { ...categoryValue } });
    } else {
      const key = Data.expenseCategories.create({ value: categoryValue! });
      addEmojiToCategoryName(key, categoryValue);
    }

    onDoneUpsertCategory();
  }

  async function addEmojiToCategoryName(key: string | undefined, categoryValue: CategoryValue) {
    if (!key) {
      return;
    }
    const emojiSuggestion = await getLLMCategoryIconSuggestion()({
      categoryName: categoryValue.name,
    });
    const icon = typeof emojiSuggestion.data === 'string' ? emojiSuggestion.data : undefined;
    Data.expenseCategories.update(key, { value: { ...categoryValue, icon } });
  }

  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('Edit Category')}
      </OpenSans.Primary>

      <Spacer.Vertical size={16} />

      <Layout.Row>
        {selectedCategoryKey ? (
          <Layout.Row>
            <LabelTextField label="Icon" transparent>
              <Layout.Column style={{ width: 40 }}>
                <Layout.Column style={{ position: 'absolute', top: 0, bottom: 0 }} justify>
                  <IconPicker
                    icon={categoryValue?.icon ?? DEFAULT_ICON}
                    onChange={(icon) => setCategoryValue({ ...categoryValue, icon })}
                  />
                </Layout.Column>
              </Layout.Column>
            </LabelTextField>
            <Spacer.Horizontal size={32} />
          </Layout.Row>
        ) : null}

        <LabelTextField
          label="Name"
          active={categoryInputFocused}
          error={!!errorMessage}
          errorMessage={errorMessage}
        >
          <OpenSans.Input
            autoFocus
            name="category name"
            value={categoryValue?.name ?? ''}
            type="text"
            placeholder="Category Name"
            grow
            style={{ outline: 'none', width: 250 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const name = event.target.value;
              setCategoryValue({ ...categoryValue, name });
            }}
            autoComplete="off"
            onFocus={() => setCategoryInputFocused(true)}
            onBlur={() => setCategoryInputFocused(false)}
          />
        </LabelTextField>

        <Spacer.Horizontal size={32} />
        <LabelTextField label="GL Code" active={false}>
          <OpenSans.Input
            name="gl code"
            value={categoryValue?.glCode ?? ''}
            type="text"
            placeholder="GL Code"
            grow
            style={{ outline: 'none', width: 150 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const glCode = event.target.value || undefined;
              setCategoryValue({ ...categoryValue, glCode });
            }}
            autoComplete="off"
          />
        </LabelTextField>

        <Spacer.Horizontal size={32} />
        <LabelTextField label="Tax Deductible" transparent>
          <Layout.Column style={{ width: 40 }}>
            <Layout.Column style={{ position: 'absolute', top: 0, bottom: 0 }} justify>
              <Checkbox
                size="large"
                style={{ padding: 0 }}
                checked={categoryValue?.deductible ?? undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const deductible = event.target.checked;
                  setCategoryValue({ ...categoryValue, deductible });
                }}
              />
            </Layout.Column>
          </Layout.Column>
        </LabelTextField>

        <Spacer.Horizontal size={32} />
        <LabelTextField label="Tip Eligible" transparent>
          <Layout.Column style={{ width: 40 }}>
            <Layout.Column style={{ position: 'absolute', top: 0, bottom: 0 }} justify>
              <Checkbox
                size="large"
                style={{ padding: 0 }}
                checked={categoryValue?.tipEligible ?? false}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const tipEligible = event.target.checked;
                  setCategoryValue({ ...categoryValue, tipEligible });
                }}
              />
            </Layout.Column>
          </Layout.Column>
        </LabelTextField>
      </Layout.Row>

      <Spacer.Vertical size={32} />

      <Layout.Row justify="flex-end">
        <Button.Secondary
          onClick={onDiscard}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 4px rgba(255, 255, 255, 1)',
          }}
        >
          <Icon
            size={15}
            color={theme.colors.brandPrimary}
            style={{ paddingRight: 10 }}
            name="chevron-back"
          />
          <OpenSans.Primary
            size={15}
            weight="bold-700"
            style={{ color: theme.colors.brandPrimary }}
          >
            {getTranslation('Discard')}
          </OpenSans.Primary>
        </Button.Secondary>
        <Spacer.Horizontal size={32} />
        <Button.Primary
          onClick={onSave}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
            width: 'auto',
          }}
        >
          <OpenSans.Custom size={15} weight="bold-700" style={{ color: theme.colors.buttonWhite }}>
            {getTranslation('Save Category')}
          </OpenSans.Custom>
          <Icon
            size={15}
            color={theme.colors.buttonWhite}
            style={{ paddingLeft: 10 }}
            name="chevron-forward"
          />
        </Button.Primary>
      </Layout.Row>
    </Layout.Column>
  );
};
